import * as React from 'react';
import SelectPrimitive, { components as SelectComponents } from 'react-select';

import { cn } from './cn';
import { useUiPortalContainer } from '@/js/wisemetering/ui/render_with_ui_root';
import { Icon } from './Icon';
import { WM_FLOATING_SURFACE_CLASSNAME } from './surfaceStyles';

// Select is bottom-of-stack — geometry/focus/disabled inline. Input
// and TextArea bake the same shape independently; drift is visible in
// code review, not hidden behind a shared module.
//
// Typography recipes are also baked locally because react-select's
// classNames callbacks expect raw class strings, not React components
// — wrapping option labels / group headings in <Text> / <Heading> is
// not on the table.
//
// `wm-field` and the CSS variables are read by Form.jsx for label /
// fieldset offsets — keep them on the rendered control.
const WM_FIELD_GEOMETRY_CLASSNAME = 'wm-field [--wm-field-border-width:1px] [--wm-field-margin:0px] [--wm-field-padding-x:0.75rem] [--wm-field-padding-y:0.5rem]';

const BODY_TEXT_CLASSNAME = 'tw-text-sm tw-leading-6';
const SMALL_TEXT_CLASSNAME = 'tw-text-xs tw-leading-5';
const CAPTION_TEXT_CLASSNAME = 'tw-text-caption tw-leading-5';
const TITLE_SM_HEADING_CLASSNAME = 'tw-text-base tw-font-semibold tw-leading-tight';

// Two stops for the control — `medium` is the 40px default,
// `small` (32px) for inline contexts. Menu / option chrome
// stays at body sizing because the dropdown panel reads better
// at consistent rhythm regardless of trigger size.
const SELECT_CONTROL_BASE_CLASSNAME = `${WM_FIELD_GEOMETRY_CLASSNAME} tw-box-border tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-1 tw-rounded-md tw-border tw-border-solid tw-border-input tw-bg-background tw-text-foreground tw-shadow-none tw-transition-colors`;

// Canonical control-height scale: small=32, medium=40 (default),
// large=44 — shared with Button / IconButton / Input / TextArea.
const SELECT_CONTROL_SIZE_CLASSNAMES = {
    small: `${SMALL_TEXT_CLASSNAME} tw-min-h-8`,
    medium: `${BODY_TEXT_CLASSNAME} tw-min-h-10`,
    large: `${BODY_TEXT_CLASSNAME} tw-min-h-11`
};

const SELECT_VALUE_CONTAINER_SIZE_CLASSNAMES = {
    small: 'tw-flex tw-flex-1 tw-flex-wrap tw-items-center tw-gap-1 tw-px-2.5 tw-py-1',
    medium: 'tw-flex tw-flex-1 tw-flex-wrap tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5',
    large: 'tw-flex tw-flex-1 tw-flex-wrap tw-items-center tw-gap-2 tw-px-3.5 tw-py-2'
};

const SELECT_MENU_CLASSNAME = `${WM_FLOATING_SURFACE_CLASSNAME} tw-z-overlay tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-muted-foreground tw-text-foreground`;
// Option / loading / empty / group-heading chrome only — text recipe and
// horizontal padding are layered on per `size` so dropdown rows align
// flush with the trigger value at any control size.
const SELECT_OPTION_CLASSNAME = 'tw-relative tw-flex tw-w-full tw-cursor-pointer tw-select-none tw-items-center tw-py-2 tw-text-foreground tw-outline-none tw-transition-colors';

// Mirrors the horizontal padding of `SELECT_VALUE_CONTAINER_SIZE_CLASSNAMES`
// so an option's text starts at the same x-offset as the trigger's selected
// value. Keep these two maps in lock-step.
const SELECT_DROPDOWN_X_PADDING_BY_SIZE = {
    small: 'tw-px-2.5',
    medium: 'tw-px-3',
    large: 'tw-px-3.5'
};
const SELECT_MULTI_VALUE_CLASSNAME = 'tw-inline-flex tw-items-center tw-rounded-full tw-border tw-border-solid tw-border-border tw-bg-muted tw-text-foreground';

const isOptionGroup = option => Array.isArray(option?.options);

const normalizeOption = option => {
    if (option === null || option === undefined) {
        return option;
    }

    if (typeof option !== 'object') {
        return {
            value: option,
            label: String(option)
        };
    }

    return {
        ...option,
        value: option.value ?? option.label ?? '',
        label: option.label ?? String(option.value ?? '')
    };
};

const normalizeOptions = options => {
    if (Array.isArray(options)) {
        return options.map(option => {
            if (!isOptionGroup(option)) {
                return normalizeOption(option);
            }

            return {
                ...option,
                label: option.label ?? option.heading ?? '',
                options: option.options.map(normalizeOption)
            };
        });
    }

    return Object.entries(options || {}).map(([key, option]) => normalizeOption({
        ...option,
        value: key,
        label: option?.label ?? key
    }));
};

const compareByLabel = (left, right, sortDirection) => {
    const leftLabel = String(left?.label ?? '');
    const rightLabel = String(right?.label ?? '');
    const comparison = leftLabel.localeCompare(rightLabel, undefined, { sensitivity: 'base' });

    return sortDirection === 'desc' ? comparison * -1 : comparison;
};

const sortOptions = (options, sortDirection) => {
    if (!sortDirection) {
        return options;
    }

    return options.map(optionOrGroup => {
        if (!isOptionGroup(optionOrGroup)) {
            return optionOrGroup;
        }

        return {
            ...optionOrGroup,
            options: [...optionOrGroup.options].sort((left, right) => compareByLabel(left, right, sortDirection))
        };
    }).sort((left, right) => {
        if (isOptionGroup(left) || isOptionGroup(right)) {
            return 0;
        }

        return compareByLabel(left, right, sortDirection);
    });
};

const getOptionCount = options => options.reduce((count, optionOrGroup) => {
    if (!isOptionGroup(optionOrGroup)) {
        return count + 1;
    }

    return count + optionOrGroup.options.length;
}, 0);

const isSearchEnabled = (search, optionCount) => {
    if (typeof search === 'number') {
        return optionCount >= search;
    }

    return !!search;
};

const DropdownIndicator = props => (
    <SelectComponents.DropdownIndicator {...props}>
        <Icon icon="chevronDown" size="small" tone="inherit" />
    </SelectComponents.DropdownIndicator>
);

const ClearIndicator = props => (
    <SelectComponents.ClearIndicator {...props}>
        <Icon icon="close" size="small" tone="inherit" />
    </SelectComponents.ClearIndicator>
);

const MultiValueRemove = props => (
    <SelectComponents.MultiValueRemove {...props}>
        <Icon icon="close" size="small" tone="inherit" />
    </SelectComponents.MultiValueRemove>
);

const Menu = props => {
    const { onMenuPlacementChange } = props.selectProps;

    React.useEffect(() => {
        onMenuPlacementChange?.(props.placement);
    }, [onMenuPlacementChange, props.placement]);

    return <SelectComponents.Menu {...props} />;
};

// `first-letter:uppercase` gives the option label sentence case so the
// i18n strings can stay lowercase (composable in copy elsewhere) while
// the rendered option reads as a proper label. Screen-reader neutral.
const Option = props => (
    <SelectComponents.Option {...props}>
        <div className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2">
            <span className="tw-min-w-0 tw-truncate first-letter:tw-uppercase">{props.children}</span>
            {props.isSelected ? (
                <Icon
                    icon="check"
                    size="small"
                    tone={props.isFocused ? 'inherit' : 'primary'}
                />
            ) : null}
        </div>
    </SelectComponents.Option>
);

// react-select inlines a few base styles via emotion that cannot be expressed
// through classNames alone (menuPortal z-index, multiValueLabel wrapping, and
// the input element's :focus box-shadow reset). Everything else is driven by
// design-token Tailwind classes via the classNames map below.
const internalStyles = {
    // react-select bakes both `minHeight: 38px` AND `padding: 2px 8px`
    // into its base control style even with `unstyled` set — it counts
    // as layout, not visual. Strip both so the Tailwind size classes
    // (`tw-min-h-8/10/11`) drive the height end-to-end and the control
    // total matches Button / Input / IconButton at the same `size`.
    control: ({ minHeight, padding, ...base }) => base,
    // react-select bakes vertical margin / padding into Input, ValueContainer,
    // SingleValue, and Placeholder via emotion. Strip those keys (rather than
    // override with `0`, which beats Tailwind) so the Tailwind class on each
    // slot drives the geometry end-to-end and the Control height matches the
    // canonical size scale.
    input: ({ margin, padding, paddingTop, paddingBottom, ...base }) => ({
        ...base,
        'input:focus': {
            boxShadow: 'none'
        }
    }),
    valueContainer: ({ padding, ...base }) => base,
    singleValue: ({ margin, ...base }) => base,
    placeholder: ({ margin, ...base }) => base,
    menuPortal: base => ({
        ...base,
        zIndex: 60
    }),
    multiValueLabel: base => ({
        ...base,
        overflow: 'visible',
        whiteSpace: 'normal'
    })
};

// Closed allow-list of props this primitive accepts. Anything else
// passed by the caller is silently dropped — we don't spread rest
// props to `react-select`. The reason is contract clarity: every
// prop here is documented in the catalog (`publicApi.js`), and the
// styling / portal / classNames behaviour we layer on top can't be
// safely combined with arbitrary react-select overrides. If you need
// a new react-select feature, add it to this signature and to
// `publicApi.js` in the same change.
const Select = React.forwardRef(function Select(
    {
        clearable = false,
        closeMenuOnSelect,
        defaultValue,
        disabled = false,
        filterOption,
        hideSelectedOptions,
        id,
        // `inlineMenu` opts out of the menu portal — the option list renders
        // as a DOM child of the Select control, anchored absolutely under it.
        // Use this when the Select itself lives inside another floating
        // surface (Popover, Dialog) — without it, the portalled menu paints
        // outside the host surface and reads as a disconnected widget.
        inlineMenu = false,
        isMulti = false,
        loadingMessage,
        name,
        noOptionsMessage,
        onBlur,
        onChange,
        // `onInputChange(term, { action })` mirrors react-select's
        // signature: `action` is "input-change" while the user types,
        // "menu-close" / "set-value" / "input-blur" when the input
        // clears or the menu closes. Pair with `filterOption={() => true}`
        // to drive a controlled-options pattern where the parent owns
        // the candidate pool and recomputes `options` reactively as the
        // user types — used by callers that need to cap, sort, or fetch
        // options based on the typed term.
        onInputChange,
        options = [],
        placeholder = i18next.t('ui.please_select_option'),
        search = false,
        size = 'medium',
        sort = null,
        value
    },
    ref
) {
    const portalContainer = useUiPortalContainer();
    const [menuPlacementState, setMenuPlacementState] = React.useState('bottom');
    const resolvedSize = SELECT_CONTROL_SIZE_CLASSNAMES[size] ? size : 'medium';

    // When the menu is inline, force it to participate in normal flow so
    // the host surface (popover, dialog, …) grows to contain it. Default
    // react-select positions the menu absolutely, which leaves the host
    // unaware of the menu's height. We also drop the menu's shadow — the
    // host already paints its own, doubling them would draw a "card
    // inside a card". The border itself is preserved so the menu visually
    // closes the control's open edge, painting control + options as a
    // single rounded box.
    const styles = React.useMemo(() => {
        if (!inlineMenu) {
            return internalStyles;
        }

        return {
            ...internalStyles,
            menu: base => ({
                ...base,
                position: 'static',
                boxShadow: 'none'
            })
        };
    }, [inlineMenu]);

    const normalizedOptions = React.useMemo(() => {
        return sortOptions(normalizeOptions(options), sort);
    }, [options, sort]);

    const optionCount = React.useMemo(() => getOptionCount(normalizedOptions), [normalizedOptions]);
    const searchable = React.useMemo(() => isSearchEnabled(search, optionCount), [optionCount, search]);

    const resolvedNoOptionsMessage = React.useCallback((messageContext) => {
        if (typeof noOptionsMessage === 'function') {
            return noOptionsMessage(messageContext);
        }

        return noOptionsMessage ?? i18next.t('ui.no_options_found');
    }, [noOptionsMessage]);

    const resolvedLoadingMessage = React.useCallback((messageContext) => {
        if (typeof loadingMessage === 'function') {
            return loadingMessage(messageContext);
        }

        return loadingMessage ?? i18next.t('ui.please_wait');
    }, [loadingMessage]);

    const internalClassNames = React.useMemo(() => ({
        clearIndicator: () => 'tw-inline-flex tw-h-6 tw-w-6 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-md tw-text-muted-foreground tw-transition-colors hover:tw-bg-muted hover:tw-text-foreground',
        container: () => 'tw-w-full',
        control: state => cn(
            SELECT_CONTROL_BASE_CLASSNAME,
            SELECT_CONTROL_SIZE_CLASSNAMES[resolvedSize],
            state.isFocused || state.menuIsOpen ? 'tw-border-muted-foreground' : null,
            // When the menu is open, the control hands off one rounded
            // edge to the menu and they paint as a single continuous box.
            // Works the same in portalled and inline mode — the menu's
            // own classNames flip the opposite edge to match.
            state.menuIsOpen && menuPlacementState === 'bottom' ? 'tw-rounded-b-none tw-border-b-0' : null,
            state.menuIsOpen && menuPlacementState === 'top' ? 'tw-rounded-t-none tw-border-t-0' : null,
            state.isDisabled ? 'tw-cursor-not-allowed tw-opacity-45' : null
        ),
        dropdownIndicator: () => 'tw-inline-flex tw-h-6 tw-w-6 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-md tw-text-muted-foreground tw-transition-colors hover:tw-bg-muted hover:tw-text-foreground',
        group: () => 'tw-p-0',
        groupHeading: () => cn(TITLE_SM_HEADING_CLASSNAME, SELECT_DROPDOWN_X_PADDING_BY_SIZE[resolvedSize], 'tw-py-1.5 tw-text-muted-foreground'),
        indicatorsContainer: () => 'tw-flex tw-shrink-0 tw-items-center tw-gap-1 tw-self-stretch tw-pr-2',
        indicatorSeparator: () => 'tw-hidden',
        input: () => cn(
            resolvedSize === 'small' ? SMALL_TEXT_CLASSNAME : BODY_TEXT_CLASSNAME,
            'tw-m-0 tw-cursor-text tw-p-0 tw-text-foreground'
        ),
        loadingMessage: () => cn(
            resolvedSize === 'small' ? SMALL_TEXT_CLASSNAME : BODY_TEXT_CLASSNAME,
            SELECT_DROPDOWN_X_PADDING_BY_SIZE[resolvedSize],
            'tw-py-2 tw-text-muted-foreground'
        ),
        menu: state => cn(
            SELECT_MENU_CLASSNAME,
            state.placement === 'bottom' ? 'tw-rounded-t-none tw-border-t-0 -tw-mt-px' : null,
            state.placement === 'top' ? 'tw-rounded-b-none tw-border-b-0 -tw-mb-px' : null
        ),
        // Native scroll on the menu list: when the options overflow the
        // 240px cap, the browser draws its scrollbar. No custom scroll
        // chevrons — users know how to scroll. The browser/OS decides
        // the scrollbar's appearance (macOS uses overlay, Windows/Linux
        // show a permanent bar) — we accept that default.
        menuList: () => 'tw-max-h-60 tw-divide-y tw-divide-border tw-overflow-y-auto tw-p-0',
        multiValue: () => SELECT_MULTI_VALUE_CLASSNAME,
        multiValueLabel: () => cn(CAPTION_TEXT_CLASSNAME, 'tw-px-2.5 tw-py-0.5 tw-font-semibold tw-text-foreground first-letter:tw-uppercase'),
        multiValueRemove: () => 'tw-mr-1 tw-inline-flex tw-h-4 tw-w-4 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-sm tw-text-muted-foreground tw-transition-colors hover:tw-text-foreground',
        noOptionsMessage: () => cn(
            resolvedSize === 'small' ? SMALL_TEXT_CLASSNAME : BODY_TEXT_CLASSNAME,
            SELECT_DROPDOWN_X_PADDING_BY_SIZE[resolvedSize],
            'tw-py-2 tw-text-muted-foreground'
        ),
        option: state => cn(
            resolvedSize === 'small' ? SMALL_TEXT_CLASSNAME : BODY_TEXT_CLASSNAME,
            SELECT_OPTION_CLASSNAME,
            SELECT_DROPDOWN_X_PADDING_BY_SIZE[resolvedSize],
            state.isFocused ? 'tw-bg-accent tw-text-foreground' : null,
            state.isSelected && !state.isFocused ? 'tw-bg-muted tw-text-foreground' : null,
            state.isDisabled ? 'tw-pointer-events-none tw-opacity-50' : null
        ),
        placeholder: () => 'tw-text-muted-foreground first-letter:tw-uppercase',
        singleValue: () => cn(
            resolvedSize === 'small' ? SMALL_TEXT_CLASSNAME : BODY_TEXT_CLASSNAME,
            'tw-font-normal tw-text-foreground first-letter:tw-uppercase'
        ),
        valueContainer: () => SELECT_VALUE_CONTAINER_SIZE_CLASSNAMES[resolvedSize]
    }), [menuPlacementState, resolvedSize]);

    return (
        <SelectPrimitive
            ref={ref}
            unstyled
            className={cn('tw-w-full')}
            classNames={internalClassNames}
            closeMenuOnSelect={closeMenuOnSelect ?? !isMulti}
            components={{
                ClearIndicator,
                DropdownIndicator,
                IndicatorSeparator: null,
                Menu,
                MultiValueRemove,
                Option
            }}
            defaultValue={defaultValue}
            filterOption={filterOption}
            hideSelectedOptions={hideSelectedOptions ?? false}
            inputId={id}
            isClearable={clearable}
            isDisabled={disabled}
            isMulti={isMulti}
            isSearchable={searchable}
            loadingMessage={resolvedLoadingMessage}
            menuPortalTarget={inlineMenu ? undefined : (portalContainer ?? undefined)}
            menuPosition={inlineMenu ? 'absolute' : 'fixed'}
            name={name}
            noOptionsMessage={resolvedNoOptionsMessage}
            onBlur={onBlur}
            onChange={onChange}
            onInputChange={onInputChange}
            onMenuPlacementChange={setMenuPlacementState}
            options={normalizedOptions}
            placeholder={placeholder}
            styles={styles}
            value={value}
        />
    );
});

export { Select };
